import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClassConstructor, instanceToPlain, plainToClass } from 'class-transformer';
import * as Cookie from 'js-cookie';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, tap } from 'rxjs/operators';

/**
 * @param params Query http params to pass.
 * @param toClass The constructor use to map result.
 */
export interface CedreHttpOption {
    params?: HttpParams;
    toClass?: ClassConstructor<any>;
}

export function getAccessToken() {
    return Cookie.get('access_token');
}

@Injectable({ providedIn: 'root' })
export class CedreHttpClient {
    httpHeaders = new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('Authorization', `Bearer ${getAccessToken()}`);

    constructor(private http: HttpClient) {}

    /**
     * Make a http GET query.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @returns
     */
    get$<T>(url: string, options: CedreHttpOption = {}): Promise<T> {
        return this.http
            .get<T>(url, { ...options, headers: this.httpHeaders })
            .pipe(
                map((data) => (options.toClass ? plainToClass(options.toClass, data) : data)),
                catchError(this.logHttpError<T>(url, 'get'))
            )
            .toPromise();
    }
    /**
     * Make a http GET query.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @returns
     */
    getPdf$<T>(url: string, options: CedreHttpOption = {}): Promise<T> {
        const httpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/pdf')
            .set('Authorization', `Bearer ${getAccessToken()}`);
        return this.http
            .get<T>(url, { ...options, headers: httpHeaders })

            .toPromise();
    }

    /**
     * Make a http GET query and cache them.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @returns
     */
    getShared$<T>(url: string, options: CedreHttpOption = {}): Observable<T> {
        console.time(`[PERF] ${url}`);
        return this.http.get<T>(url, { ...options, headers: this.httpHeaders }).pipe(
            tap(() => console.timeEnd(`[PERF] ${url}`)),
            tap(() => console.time(`[PERF] Mapping ${url}`)),
            map((data) => (options.toClass ? plainToClass(options.toClass, data) : data)),
            tap(() => console.timeEnd(`[PERF] Mapping ${url}`)),
            filter((v) => !!v),
            shareReplay(1),
            catchError(this.logHttpError<T>(url, 'get'))
        );
    }

    /**
     * Make a http DELETE query.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @returns
     */
    delete<T>(url: string, options: CedreHttpOption = {}): Promise<T> {
        return this.http
            .delete<T>(url, { ...options, headers: this.httpHeaders })
            .pipe(
                map((data) => (options.toClass ? plainToClass(options.toClass, data) : data)),
                catchError(this.logHttpError<T>(url, 'delete'))
            )
            .toPromise();
    }

    /**
     * Make a http POST query.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @param body content send on the payload.
     * @returns
     */
    post<T>(url: string, body: any | null, options: CedreHttpOption = {}): Promise<T> {
        return this.http
            .post<T>(url, instanceToPlain(body), {
                ...options,
                headers: this.httpHeaders,
            })
            .pipe(
                map((data) => (options.toClass ? plainToClass(options.toClass, data) : data)),
                catchError(this.logHttpError<T>(url, 'post'))
            )
            .toPromise();
    }

    /**
     * Make a http POST query with a blob response.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @param body content send on the payload.
     * @returns
     */
    getBlob<T>(url: string, body: any | null, options: CedreHttpOption = {}): Promise<T> {
        return this.http
            .post(url, instanceToPlain(body), {
                ...options,
                headers: this.httpHeaders,
                responseType: 'blob',
                observe: 'response',
            })
            .pipe(
                map((data) => (options.toClass ? plainToClass(options.toClass, data) : data)),
                catchError(this.logHttpError<T>(url, 'post'))
            )
            .toPromise();
    }

    /**
     * Make a http PATCH query.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @param body content send on the payload.
     * @returns
     */
    patch<T>(url: string, body: any | null, options: CedreHttpOption = {}): Promise<T> {
        return this.http
            .patch<T>(url, instanceToPlain(body), {
                ...options,
                headers: this.httpHeaders,
            })
            .pipe(
                map((data) => (options.toClass ? plainToClass(options.toClass, data) : data)),
                catchError(this.logHttpError<T>(url, 'patch'))
            )
            .toPromise();
    }

    /**
     * Make a http PUT query.
     * @param url url of service.
     * @param options options to used with the xhr request.
     * @param body content send on the payload.
     * @returns
     */
    put<T>(url: string, body: any | null, options: CedreHttpOption = {}): Promise<T> {
        return this.http
            .put<T>(url, instanceToPlain(body), {
                ...options,
                headers: this.httpHeaders,
            })
            .pipe(
                map((data) => (options.toClass ? plainToClass(options.toClass, data) : data)),
                catchError(this.logHttpError<T>(url, 'put'))
            )
            .toPromise();
    }

    private logHttpError<T>(url: string, method: string) {
        return (err) => {
            console.error(`CedreHttpClient error from url ${url} and method ${method}`, err);
            return throwError(err);
        };
    }
}
